import axios from "axios";
import { API } from "@/context/AuthContext";
import { logger } from "@/lib/logger";

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    return reg;
  } catch (e) {
    logger.warn("SW register failed", e);
    return null;
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function isIosStandalone() {
  // iOS PWA installed to home screen
  return window.navigator.standalone === true;
}

export function isIos() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export async function getPushPermissionState() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function subscribeAdminPush() {
  if (!isPushSupported()) throw new Error("Push not supported in this browser");
  if (isIos() && !isIosStandalone()) {
    throw new Error(
      "On iPhone: tap Share → Add to Home Screen first, then open the app icon and try again."
    );
  }

  const reg = await navigator.serviceWorker.ready;
  const { data } = await axios.get(`${API}/push/vapid-public-key`, { withCredentials: true });
  const key = data.key;
  if (!key) throw new Error("VAPID key not configured on server");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notification permission denied");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    });
  }

  const payload = sub.toJSON();
  await axios.post(
    `${API}/push/subscribe`,
    { endpoint: payload.endpoint, keys: payload.keys },
    { withCredentials: true }
  );
  return sub;
}

export async function unsubscribeAdminPush() {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const payload = sub.toJSON();
  try {
    await axios.post(
      `${API}/push/unsubscribe`,
      { endpoint: payload.endpoint, keys: payload.keys },
      { withCredentials: true }
    );
  } catch (e) {
    // Server-side cleanup is best-effort; the local unsubscribe below is what matters.
    logger.warn("Push unsubscribe (server) failed:", e);
  }
  await sub.unsubscribe();
}

export async function sendTestPush() {
  await axios.post(`${API}/push/test`, {}, { withCredentials: true });
}

export async function isCurrentlySubscribed() {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}
